import { useState } from 'react';
import { combineDateTime, MONTHS, monthGrid, shiftMonth, type YearMonth } from './calendar';

const sameDay = (a: Date | null, b: Date | null): boolean =>
  a !== null && b !== null && a.toDateString() === b.toDateString();

/** UI-17: a month calendar plus a time field; the value is a `datetime-local` string. */
export function DateTimePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const picked = value ? new Date(value) : null;
  const [view, setView] = useState<YearMonth>(() => {
    const d = picked ?? new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const time = value.includes('T') ? value.slice(11, 16) : '09:00';
  const today = new Date();
  return (
    <div className="calendar" data-testid="calendar">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-sm"
          aria-label="previous month"
          onClick={() => setView(shiftMonth(view, -1))}
        >
          ‹
        </button>
        <strong>
          {MONTHS[view.month]} {view.year}
        </strong>
        <button
          type="button"
          className="btn btn-sm"
          aria-label="next month"
          onClick={() => setView(shiftMonth(view, 1))}
        >
          ›
        </button>
      </div>
      <table className="table calendar-grid">
        <thead>
          <tr>
            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
              <th key={d}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthGrid(view.year, view.month).map((week, wi) => (
            <tr key={`w${view.year}-${view.month}-${wi}`}>
              {week.map((day, di) => (
                <td key={day ? day.toISOString() : `blank-${wi}-${di}`}>
                  {day ? (
                    <button
                      type="button"
                      className={`btn btn-sm ${sameDay(day, picked) ? 'btn-primary' : ''}`}
                      aria-pressed={sameDay(day, picked)}
                      aria-label={day.toDateString()}
                      onClick={() => onChange(combineDateTime(day, time))}
                    >
                      {sameDay(day, today) ? <strong>{day.getDate()}</strong> : day.getDate()}
                    </button>
                  ) : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <label className="row" style={{ alignItems: 'center', gap: 8 }}>
        <span className="small">Time</span>
        <input
          className="input"
          type="time"
          value={time}
          onChange={(e) => onChange(combineDateTime(picked ?? today, e.target.value))}
        />
      </label>
      <p className="muted small">
        {picked ? `Scheduled for ${picked.toLocaleString()}` : 'Pick a day'}
      </p>
    </div>
  );
}
